import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Calendar, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import ReactMarkdown from "react-markdown";

const BlogPostPage = ({ seoSettings }) => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${id}`);
      setPost(response.data);
    } catch (error) {
      console.error("Error fetching blog post:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="pt-20 lg:pt-[118px] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B7355] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-20 lg:pt-[118px] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Post not found</p>
          <Link to="/blog">
            <Button variant="outline" data-testid="back-to-blog-button">
              <ArrowLeft size={16} className="mr-2" /> Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 lg:pt-[118px] min-h-screen">
      <SEO 
        title={post?.seo_title || `${post?.title || 'Blog Post'} - ${seoSettings?.seo_title || 'Mélange Mobile Bar'}`}
        description={post?.excerpt || post?.content?.substring(0, 150) || seoSettings?.seo_description}
        keywords={post?.seo_keywords || seoSettings?.seo_keywords}
        ogImage={post?.image_url || seoSettings?.seo_og_image}
      />
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/blog">
          <Button variant="outline" data-testid="back-to-blog-link">
            <ArrowLeft size={16} className="mr-2" /> Back to Blog
          </Button>
        </Link>
      </div>

      {/* Post Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="mb-8">
          <div className="flex items-center text-sm text-gray-600 mb-4">
            <Calendar size={16} className="mr-2" />
            <span data-testid="post-date">{formatDate(post.created_at)}</span>
            <span className="mx-2">•</span>
            <span data-testid="post-author">By {post.author}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" data-testid="post-title">
            {post.title}
          </h1>
        </div>

        {/* Featured Image - True Auto-fit (no forced aspect ratio) */}
        {post.image_url ? (
          <div className="mb-8 w-full rounded-lg overflow-hidden shadow-lg" data-testid="post-featured-image">
            <img 
              src={post.image_url} 
              alt={post.title}
              className="w-full h-auto object-contain"
              style={{
                maxHeight: '600px',
                display: 'block'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<div class="h-96 bg-gradient-to-br from-[#D4C4B0] to-[#8B7355] rounded-lg flex items-center justify-center"><div class="text-9xl">🍹</div></div>';
              }}
            />
          </div>
        ) : (
          <div className="mb-8 h-96 bg-gradient-to-br from-[#D4C4B0] to-[#8B7355] rounded-lg flex items-center justify-center shadow-lg" data-testid="post-featured-image">
            <div className="text-9xl">🍹</div>
          </div>
        )}

        {/* Post Content - Clean formatting that preserves pasted content */}
        <div className="prose prose-lg max-w-none 
          prose-headings:text-gray-900 
          prose-h1:text-4xl prose-h1:font-bold prose-h1:mb-4 prose-h1:mt-8
          prose-h2:text-3xl prose-h2:font-semibold prose-h2:mb-3 prose-h2:mt-6 
          prose-h3:text-2xl prose-h3:font-semibold prose-h3:mb-2 prose-h3:mt-5
          prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
          prose-strong:font-bold prose-strong:text-gray-900
          prose-ul:my-4 prose-ol:my-4
          prose-li:text-gray-700
          prose-a:text-[#8B7355] prose-a:underline
          " data-testid="post-content">
          <ReactMarkdown>
            {post.content}
          </ReactMarkdown>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
